export default {
  privacy: {
    title: 'Politique de confidentialité',
    lastUpdated: 'Dernière mise à jour :',
    lastUpdatedDate: '16 octobre 2026',
    backToHome: '← Retour à l\'accueil',
    seoDescription:
      'Découvrez comment Geofleet.ai collecte, utilise et protège vos informations, y compris les intégrations Google Workspace et Google Sheets, les limites IA/ML et vos droits en matière de confidentialité.',
    intro: [
      'Geofleet.ai (« Geofleet », « nous », « notre » ou « nos ») respecte votre vie privée et s\'engage à protéger les informations qui lui sont confiées.',
      'La présente Politique de confidentialité explique comment nous collectons, utilisons, divulguons, conservons et protégeons les informations lorsque vous accédez ou utilisez le site web Geofleet.ai, les applications web, les applications mobiles, les API, les intégrations et les services associés (collectivement, les « Services »).',
      'En utilisant les Services, vous reconnaissez les pratiques décrites dans la présente Politique de confidentialité.',
    ],
    sections: {
      informationWeCollect: {
        title: '1. Informations que nous collectons',
        intro: 'Selon la manière dont vous utilisez les Services, nous pouvons collecter les catégories d\'informations suivantes.',
        account: {
          title: '1.1 Informations de compte et de profil',
          intro: 'Cela peut inclure :',
          items: [
            'Nom',
            'Adresse e-mail',
            'Numéro de téléphone',
            'Nom de l\'entreprise ou de l\'organisation',
            'Titre du poste',
            'Identifiants de compte et informations d\'authentification',
            'Préférences utilisateur',
            'Autres informations fournies volontairement lors de l\'inscription ou de l\'administration du compte',
          ],
        },
        delivery: {
          title: '1.2 Informations de livraison et opérationnelles',
          intro: 'Selon les fonctionnalités utilisées, nous pouvons traiter :',
          items: [
            'Informations de livraison et de commande',
            'Informations clients et destinataires',
            'Adresses de livraison',
            'Informations sur les conducteurs',
            'Informations sur les véhicules',
            'Localisations des conducteurs et des véhicules',
            'Itinéraires et trajets',
            'Statut de livraison',
            'Preuve de livraison',
            'Signatures électroniques',
            'OTP et informations de vérification',
            'Informations d\'odomètre',
            'Informations de géorepérage',
            'Notes et enregistrements opérationnels',
            'Autres informations soumises par les clients ou utilisateurs via les Services',
          ],
        },
        device: {
          title: '1.3 Informations techniques et sur l\'appareil',
          intro: 'Nous pouvons collecter automatiquement :',
          items: [
            'Adresse IP',
            'Type de navigateur',
            'Système d\'exploitation',
            'Type d\'appareil',
            'Identifiants d\'appareil',
            'Informations de journal',
            'Informations sur l\'application',
            'Localisation approximative dérivée d\'informations techniques le cas échéant',
            'Informations d\'utilisation et d\'interaction',
          ],
        },
        payment: {
          title: '1.4 Informations de paiement et de facturation',
          intro: 'Le cas échéant, nous pouvons traiter :',
          items: [
            'Informations de facturation',
            'Informations d\'abonnement',
            'Informations de transaction',
            'Statut de paiement',
          ],
          note:
            'Les informations de carte de paiement peuvent être traitées directement par des prestataires de paiement tiers. Nous ne stockons pas nécessairement les informations complètes de carte de paiement sur nos systèmes.',
        },
        communications: {
          title: '1.5 Communications',
          intro: 'Nous pouvons collecter les informations contenues dans :',
          items: [
            'Demandes de support',
            'E-mails',
            'Commentaires',
            'Communications de service',
            'Autres communications avec Geofleet',
          ],
        },
      },
      googleWorkspace: {
        title: '2. Données Google Workspace et Google Sheets',
        intro: [
          'Geofleet propose une intégration optionnelle avec Google Workspace qui permet aux utilisateurs autorisés d\'importer des informations depuis Google Sheets vers Geofleet.',
          'Lorsqu\'un utilisateur connecte un compte Google à Geofleet, nous n\'accédons qu\'aux informations Google Workspace nécessaires pour fournir la fonctionnalité d\'importation Google Sheets demandée par l\'utilisateur.',
          'L\'intégration utilise les autorisations d\'API Google suivantes :',
        ],
        drive: {
          title: 'Métadonnées Google Drive — drive.metadata.readonly',
          paragraphs: [
            'Geofleet utilise un accès en lecture seule aux métadonnées Google Drive pour découvrir et lister les fichiers disponibles dans Google Drive de l\'utilisateur, y compris Google Sheets.',
            'Cela permet à Geofleet d\'identifier des informations telles que :',
          ],
          items: [
            'Nom du tableur',
            'ID du fichier',
            'Type de fichier',
            'Autres métadonnées de fichier pertinentes nécessaires pour identifier le tableur',
          ],
          closing: [
            'Cette autorisation permet à l\'utilisateur de sélectionner la Google Sheet qu\'il souhaite utiliser.',
            'Geofleet n\'utilise pas cette autorisation pour créer, modifier ou supprimer des fichiers dans Google Drive de l\'utilisateur.',
          ],
        },
        sheets: {
          title: 'Google Sheets en lecture seule — spreadsheets.readonly',
          paragraphs: [
            'Après la sélection d\'un tableur par l\'utilisateur, Geofleet utilise un accès en lecture seule pour lire le contenu de la Google Sheet sélectionnée et de l\'onglet choisi.',
            'Les informations sont chargées dans l\'interface d\'importation Geofleet afin que l\'utilisateur puisse :',
          ],
          numbered: [
            'Examiner les informations du tableur.',
            'Sélectionner l\'onglet approprié.',
            'Mapper les colonnes du tableur aux champs Geofleet.',
            'Terminer l\'importation demandée.',
          ],
          closing: ['Geofleet ne modifie ni ne supprime les données Google Sheets de l\'utilisateur.'],
        },
      },
      googleProcessing: {
        title: '3. Traitement et stockage des données Google Workspace',
        paragraphs: [
          'Les informations Google Workspace accessibles via l\'intégration sont traitées uniquement pour fournir la fonctionnalité d\'importation Google Sheets demandée par l\'utilisateur et les fonctionnalités Geofleet associées.',
          'Le tableur Google source et son contenu ne sont pas conservés comme copie séparée dans le backend de Geofleet dans le cadre du processus d\'importation Google Sheets.',
          'Lorsqu\'un utilisateur importe volontairement des informations dans Geofleet, ces informations peuvent devenir partie du compte Geofleet de l\'utilisateur et être stockées comme données opérationnelles Geofleet.',
          'Par exemple, si un utilisateur importe des fiches conducteurs depuis une Google Sheet, les informations conducteur résultantes peuvent être stockées dans Geofleet afin que l\'utilisateur puisse les utiliser et les gérer via les Services.',
          'Ces informations importées sont traitées conformément à la présente Politique de confidentialité et aux exigences contractuelles et de conservation applicables.',
          'Geofleet n\'utilise pas les données Google Workspace à des fins publicitaires, de courtage de données ou de vente à des tiers.',
        ],
      },
      googleLimitedUse: {
        title: '4. Utilisation limitée des services d\'API Google',
        paragraphs: [
          'L\'utilisation par Geofleet des informations reçues des API Google respecte la Google API Services User Data Policy, y compris les exigences d\'utilisation limitée (Limited Use).',
          'Geofleet ne vend pas les données utilisateur Google.',
          'Les données Google Workspace obtenues via les API Google ne sont pas utilisées pour créer, entraîner ou améliorer des modèles d\'intelligence artificielle ou d\'apprentissage automatique généralisés ou fondamentaux.',
          'Geofleet ne transfère pas les données Google Workspace à des services d\'IA ou d\'apprentissage automatique tiers dans le but d\'entraîner ou d\'améliorer leurs modèles.',
          'Les données Google Workspace sont utilisées uniquement pour les fonctionnalités destinées à l\'utilisateur décrites dans la présente Politique de confidentialité et aux fins autorisées par les politiques applicables des services d\'API Google.',
        ],
      },
      howWeUse: {
        title: '5. Comment nous utilisons les informations',
        intro: 'Nous pouvons utiliser les informations collectées via les Services pour :',
        items: [
          'Fournir et exploiter les Services.',
          'Créer et gérer les comptes utilisateurs.',
          'Traiter les commandes, livraisons, itinéraires, trajets et flux opérationnels.',
          'Fournir les intégrations et fonctionnalités demandées.',
          'Traiter les importations initiées par l\'utilisateur.',
          'Fournir le support client.',
          'Communiquer avec les utilisateurs au sujet des Services.',
          'Traiter les abonnements et paiements.',
          'Maintenir, sécuriser et dépanner les Services.',
          'Détecter et prévenir la fraude, les abus et les accès non autorisés.',
          'Surveiller les performances et la fiabilité du système.',
          'Analyser l\'utilisation des Services.',
          'Améliorer les fonctionnalités et l\'expérience utilisateur des Services.',
          'Respecter les obligations légales applicables.',
          'Faire respecter nos Conditions d\'utilisation et autres accords applicables.',
        ],
        note:
          'Les informations obtenues via les API Google Workspace sont utilisées uniquement aux fins autorisées par la Google API Services User Data Policy applicable et les exigences d\'utilisation limitée.',
      },
      aiMl: {
        title: '6. Intelligence artificielle et apprentissage automatique',
        paragraphs: [
          'Geofleet peut fournir des fonctionnalités d\'IA, d\'automatisation, d\'analyse, de recommandation ou d\'aide à la décision dans le cadre des Services.',
          'Lorsque vous utilisez un assistant IA dans les Services, comme le Copilot de GeoFleet Lite, nous conservons vos messages et les réponses de l\'assistant avec le compte de votre organisation. Nous les utilisons pour afficher l\'historique de vos conversations sur tous vos appareils, et pour améliorer et entraîner les fonctionnalités d\'IA de Geofleet. Les données Google Workspace n\'en font jamais partie. Votre organisation peut nous demander de supprimer cet historique en contactant le support.',
          'Les données Google Workspace obtenues via les API Google ne sont pas utilisées pour entraîner, affiner, créer ou améliorer des modèles IA/ML généralisés ou fondamentaux.',
          'Les données Google Workspace ne sont pas non plus transférées à des fournisseurs IA/ML tiers dans le but d\'entraîner ou d\'améliorer leurs modèles.',
          'Toute fonctionnalité d\'IA ou d\'automatisation impliquant des informations obtenues via les API Google Workspace est soumise aux limitations et exigences décrites dans la présente Politique de confidentialité et aux politiques Google applicables.',
        ],
      },
      sharing: {
        title: '7. Partage et divulgation',
        intro: 'Nous pouvons divulguer des informations dans les circonstances suivantes.',
        serviceProviders: {
          title: 'Prestataires de services',
          paragraphs: [
            'Nous pouvons faire appel à des prestataires de services tiers de confiance pour nous aider à héberger, exploiter, sécuriser, maintenir et fournir les Services.',
            'Ces prestataires peuvent traiter des informations pour notre compte et sont censés n\'utiliser ces informations que pour les services qu\'ils fournissent à Geofleet, sous réserve d\'obligations contractuelles et de sécurité appropriées.',
          ],
        },
        legal: {
          title: 'Exigences légales',
          intro: 'Nous pouvons divulguer des informations lorsque cela est raisonnablement nécessaire pour :',
          items: [
            'Respecter la loi applicable ou une procédure légale.',
            'Répondre aux demandes légitimes des autorités gouvernementales.',
            'Protéger les droits, la propriété ou la sécurité de Geofleet, de nos utilisateurs ou d\'autrui.',
            'Détecter, prévenir ou enquêter sur la fraude ou les incidents de sécurité.',
          ],
        },
        business: {
          title: 'Transactions commerciales',
          paragraphs: [
            'Les informations peuvent être transférées dans le cadre d\'une fusion, acquisition, financement, restructuration, vente d\'actifs ou transaction corporative similaire, sous réserve des exigences légales applicables.',
          ],
        },
        authorization: {
          title: 'Avec votre autorisation',
          paragraphs: [
            'Nous pouvons divulguer des informations lorsque vous nous y avez spécifiquement autorisés ou instruits.',
            'Nous ne vendons pas d\'informations personnelles à des fins de marketing de tiers.',
            'Nous ne fournissons pas de données Google Workspace à des tiers pour la publicité, la vente, le courtage de données ou l\'entraînement de modèles IA/ML généralisés ou fondamentaux.',
          ],
        },
      },
      googleAuth: {
        title: '8. Autorisation Google et révocation',
        paragraphs: [
          'Lorsque vous connectez votre compte Google à Geofleet, vous autorisez Geofleet à accéder aux informations Google Workspace décrites lors du processus d\'autorisation Google.',
          'Vous pouvez révoquer l\'autorisation Google de Geofleet à tout moment via les paramètres de votre compte Google ou les fonctionnalités Geofleet disponibles.',
          'Après révocation de l\'autorisation, Geofleet ne pourra plus accéder aux informations Google Workspace à l\'aide de l\'autorisation révoquée.',
          'La révocation de l\'autorisation Google ne supprime pas automatiquement les informations que vous avez précédemment et volontairement importées dans Geofleet.',
          'Les données Geofleet précédemment importées restent soumises aux procédures de conservation et de suppression Geofleet applicables.',
        ],
      },
      retention: {
        title: '9. Conservation des données',
        intro: 'Nous conservons les informations aussi longtemps que raisonnablement nécessaire pour :',
        items: [
          'Fournir les Services.',
          'Maintenir les comptes clients.',
          'Remplir les obligations contractuelles.',
          'Fournir le support client.',
          'Satisfaire aux exigences légales, comptables ou réglementaires.',
          'Résoudre les litiges.',
          'Prévenir la fraude et les abus.',
          'Faire respecter nos accords.',
        ],
        paragraphs: [
          'Le tableur Google source et son contenu ne sont pas conservés comme copie séparée dans notre backend dans le cadre du processus d\'importation Google Sheets.',
          'Les informations volontairement importées dans Geofleet peuvent être conservées selon les exigences applicables de compte, d\'abonnement, opérationnelles, légales et contractuelles.',
          'Lorsque les informations ne sont plus raisonnablement nécessaires, nous pouvons les supprimer ou les anonymiser conformément à nos procédures de conservation et à la loi applicable.',
        ],
      },
      security: {
        title: '10. Sécurité',
        paragraphs: [
          'Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables conçues pour protéger les informations contre l\'accès, l\'utilisation, l\'altération, la divulgation ou la destruction non autorisés.',
          'Selon le service et l\'environnement applicables, les mesures de protection peuvent inclure :',
        ],
        items: [
          'Chiffrement en transit.',
          'Chiffrement au repos le cas échéant.',
          'Contrôles d\'accès.',
          'Mécanismes d\'authentification.',
          'Permissions basées sur les rôles.',
          'Surveillance et journalisation.',
          'Contrôles de sécurité de l\'infrastructure.',
          'Mécanismes de sauvegarde et de récupération.',
          'Évaluations de sécurité et contrôles opérationnels.',
        ],
        note: 'Aucune méthode de transmission, de traitement ou de stockage ne peut être garantie comme entièrement sécurisée.',
      },
      rights: {
        title: '11. Vos droits et choix',
        intro:
          'Selon la loi applicable et votre localisation, vous pouvez disposer de droits relatifs à vos informations personnelles, notamment le droit de :',
        items: [
          'Accéder aux informations que nous détenons à votre sujet.',
          'Demander la correction d\'informations inexactes.',
          'Demander la suppression d\'informations.',
          'Demander la limitation de certains traitements.',
          'Vous opposer à certains traitements.',
          'Demander la portabilité de certaines informations.',
          'Retirer votre consentement lorsque le traitement est fondé sur le consentement.',
          'Déposer une plainte auprès d\'une autorité de protection des données compétente.',
        ],
        contactIntro: 'Pour exercer vos droits applicables ou soumettre une demande relative à la confidentialité, contactez-nous à :',
        verify: 'Nous pouvons avoir besoin de vérifier votre identité avant de répondre à certaines demandes.',
      },
      cookies: {
        title: '12. Cookies et technologies similaires',
        intro: 'Nous et nos prestataires de services pouvons utiliser des cookies, balises web, pixels, stockage local et technologies similaires pour :',
        items: [
          'Exploiter les Services.',
          'Mémoriser les préférences.',
          'Maintenir l\'authentification.',
          'Comprendre l\'utilisation.',
          'Améliorer les performances.',
          'Détecter les problèmes de sécurité.',
          'Analyser le trafic du site web.',
        ],
        note:
          'Vous pouvez contrôler les cookies via les paramètres de votre navigateur. La désactivation de certains cookies peut affecter la disponibilité ou le fonctionnement de certaines parties des Services.',
      },
      international: {
        title: '13. Transferts internationaux de données',
        paragraphs: [
          'Geofleet et ses prestataires de services peuvent traiter des informations dans des pays autres que celui dans lequel vous résidez.',
          'Le cas échéant, nous prenons des mesures raisonnables pour garantir que les transferts internationaux sont soumis aux garanties appropriées exigées par la loi applicable.',
        ],
      },
      children: {
        title: '14. Confidentialité des enfants',
        paragraphs: [
          'Les Services sont destinés aux utilisateurs professionnels et ne s\'adressent pas aux personnes de moins de 18 ans.',
          'Nous ne collectons pas sciemment d\'informations personnelles directement auprès d\'enfants de moins de 18 ans.',
          'Si vous pensez qu\'un enfant nous a fourni des informations personnelles, veuillez nous contacter à :',
        ],
        closing:
          'Nous prendrons des mesures raisonnables pour enquêter et supprimer ces informations le cas échéant.',
      },
      thirdParty: {
        title: '15. Services tiers',
        paragraphs: [
          'Les Services peuvent contenir des liens vers ou des intégrations avec des sites web, applications, API et services tiers.',
          'Les services tiers sont régis par leurs propres conditions et politiques de confidentialité.',
          'Geofleet n\'est pas responsable des pratiques de confidentialité des services tiers qui ne sont pas contrôlés par Geofleet.',
          'Lorsque vous activez une intégration, vous devez consulter la politique de confidentialité et les conditions du prestataire tiers applicable.',
        ],
      },
      changes: {
        title: '16. Modifications de la présente Politique de confidentialité',
        paragraphs: [
          'Nous pouvons mettre à jour périodiquement la présente Politique de confidentialité pour refléter les évolutions de nos Services, de la technologie, des exigences légales ou des pratiques commerciales.',
          'Lorsque nous apportons des modifications, nous mettrons à jour la date de « Dernière mise à jour » en haut de la présente Politique de confidentialité.',
          'Le cas échéant, les modifications importantes peuvent également être communiquées par e-mail, via les Services ou par un autre moyen raisonnable.',
          'Votre utilisation continue des Services après l\'entrée en vigueur d\'une Politique de confidentialité mise à jour constitue une reconnaissance de la politique mise à jour dans la mesure permise par la loi applicable.',
        ],
      },
      contact: {
        title: '17. Nous contacter',
        intro:
          'Si vous avez des questions, préoccupations ou demandes relatives à la présente Politique de confidentialité ou à nos pratiques de confidentialité, veuillez nous contacter :',
        company: 'Geofleet.ai',
        emailLabel: 'E-mail :',
        websiteLabel: 'Site web :',
        website: 'https://geofleet.ai',
      },
    },
  },
};
